import React, { FC, Ref, CSSProperties } from 'react';
import cn from 'classnames';
import { createPopper, Options, VirtualElement } from '@popperjs/core';
import { Portal, IPortalProps } from '../Portal';
import { useForkRef } from '../../hooks/useForkRef';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';
require("./Popper.css");
var styles = {
  "Root": "Popper-module-Root_3Pv",
  "fadeIn": "Popper-module-fadeIn_itB"
}; /**
    * Базовый компонент всплывающего окна, привязанного к другому опорному ui элементу
    * без спечифичных стилей и настроек модификаторов.
    * Используется сторонняя библиотека [@popperjs/core](https://github.com/popperjs/popper-core)
    */
export const Popper = props => {
  const {
    children,
    className,
    controlRef,
    id,
    innerRef,
    isVisible = false,
    keepMounted = true,
    modifiers,
    placement: initialPlacement = 'bottom',
    role = 'tooltip',
    scope = 'withoutportal',
    strategy = 'absolute',
    style,
    target
  } = props;
  const popupRef = React.useRef(null);
  const ownRef = useForkRef(popupRef, innerRef || controlRef);
  useIsomorphicLayoutEffect(() => {
    if (!popupRef.current || !target || !isVisible) {
      return;
    }
    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
      if (target && target instanceof HTMLElement) {
        const rect = target.getBoundingClientRect();
        if (rect.top === 0 && rect.left === 0 && rect.right === 0 && rect.bottom === 0) {
          console.warn(['UI Popper: свойство `target` содержит элемент с нулевой позицией относительно viewport', 'Элемент должен присутствовать в документе и не иметь css свойство display: none'].join('\n'));
        }
      }
    }
    let popperModifiers = [{
      name: 'preventOverflow',
      enabled: true,
      options: {
        altBoundary: scope === 'withoutportal'
      }
    }, {
      name: 'flip',
      enabled: true,
      options: {
        altBoundary: scope === 'withoutportal'
      }
    }];
    if (modifiers != null) {
      popperModifiers = [...popperModifiers, ...modifiers];
    }
    createPopper(target, popupRef.current, {
      placement: initialPlacement,
      strategy,
      modifiers: popperModifiers
    });
  }, [target, scope, modifiers, isVisible, initialPlacement, strategy]);
  return /*#__PURE__*/React.createElement(Portal, {
    scope: scope,
    keepMounted: keepMounted,
    visible: true
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.Root, className),
    id: id,
    ref: ownRef,
    role: role,
    "aria-hidden": !isVisible,
    style: {
      position: 'absolute',
      top: 0,
      left: 0,
      ...(!isVisible && keepMounted && {
        display: 'none'
      }),
      ...style
    }
  }, children));
};
if (process.env.NODE_ENV !== 'production') {
  Popper.displayName = 'Popper';
}